// Repositorio de datos para las tablas `leads`, `lead_activities`,
// `lead_source_options` y `lead_assignment_config`.
// Cada función encapsula una query Supabase COMPLETA y devuelve el resultado tal cual
// (el objeto `{ data, error }` de supabase-js), para que el call site solo cambie
// `supabase.from("leads")...` por `repoFn(...)` sin tocar su manejo de { data, error }.
import { supabase } from "../../supabase";
import type { TablesInsert, TablesUpdate } from "../../types/database.types";

// ────────────────────────────────────────────────────────────────────────────
// leads — lecturas
// ────────────────────────────────────────────────────────────────────────────

/**
 * Lista todos los leads con la propiedad relacionada, ordenados por creación (desc).
 * Cubre: app/routes/crm.tsx:86-89 (clientLoader, leadsRes).
 */
export async function listLeadsWithProperty() {
  return await supabase
    .from("leads")
    .select("*, properties(id, title, price)")
    .order("created_at", { ascending: false });
}

/**
 * Lista todos los leads con la propiedad relacionada, ordenados por último contacto
 * (desc, nulls al final).
 * Cubre: app/routes/contactos.tsx:204-207 (clientLoader, contactsRes).
 */
export async function listLeadsWithPropertyByLastContact() {
  return await supabase
    .from("leads")
    .select("*, properties(id, title, price)")
    .order("last_contact_at", { ascending: false, nullsFirst: false });
}

/**
 * Lista los leads del usuario, ordenados por creación (desc).
 * Cubre: app/routes/home.tsx:33 (clientLoader, leadsRes).
 */
export async function listLeadsByUser(userId: string) {
  return await supabase
    .from("leads")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });
}

/**
 * Lista los leads asociados a una propiedad, ordenados por creación (desc).
 * Cubre: app/routes/propiedad-detalle.tsx:76 (clientLoader, leadsRes).
 */
export async function listLeadsByProperty(propertyId: string) {
  return await supabase
    .from("leads")
    .select("*")
    .eq("property_id", propertyId)
    .order("created_at", { ascending: false });
}

/**
 * Lista los leads para el selector de campañas de email, ordenados por nombre.
 * Cubre: app/routes/email-campanas-nueva.tsx:54-57 (clientLoader, leadsRes).
 */
export async function listLeadsForEmailCampaign() {
  return await supabase
    .from("leads")
    .select("id,name,email,phone,budget,status,source")
    .order("name");
}

/**
 * Lista los leads (id y nombre) para el selector de tareas, ordenados por nombre.
 * Cubre: app/routes/tareas.tsx:40-43 (clientLoader, leadsRes).
 */
export async function listLeadsIdName() {
  return await supabase
    .from("leads")
    .select("id, name")
    .order("name");
}

/**
 * Obtiene un lead con la propiedad relacionada por id (single).
 * Cubre: app/routes/contacto-detalle.tsx:226-230 (clientLoader, contactRes).
 */
export async function getLeadWithProperty(id: string) {
  return await supabase
    .from("leads")
    .select("*, properties(id, title, price)")
    .eq("id", id)
    .single();
}

/**
 * Obtiene un lead completo por id (single). Usado en la fusión de leads.
 * Cubre: app/routes/crm.tsx:353 y app/routes/crm.tsx:354 (merge_leads, keepRes/dropRes).
 */
export async function getLeadById(id: string) {
  return await supabase.from("leads").select("*").eq("id", id).single();
}

/**
 * Busca posibles leads duplicados por teléfono/email (filtro OR), limitado a 5.
 * Cubre: app/routes/crm.tsx:336-340 (check_duplicates).
 */
export async function findDuplicateLeads(orFilters: string) {
  return await supabase
    .from("leads")
    .select("id, name, phone, email, status, source, created_at")
    .or(orFilters)
    .limit(5);
}

// ────────────────────────────────────────────────────────────────────────────
// leads — inserts
// ────────────────────────────────────────────────────────────────────────────

/**
 * Inserta uno o varios leads (payload array, tal cual lo arman los call sites con `[{...}]`).
 * No devuelve la fila creada.
 * Cubre: app/components/Layout.tsx:54 (insertLead),
 *        app/components/CalculadoraHipotecaria.tsx:100 (envío calculadora),
 *        app/routes/crm.tsx:399 (create lead por defecto),
 *        app/routes/contactos.tsx:247 (intent "create"),
 *        app/routes/asesor-sitio.tsx:57 (clientAction),
 *        app/routes/propiedad-publica.tsx:88 (clientAction),
 *        app/routes/propiedad-landing.tsx:98 (clientAction).
 */
export async function insertLeads(payload: TablesInsert<"leads">[]) {
  return await supabase.from("leads").insert(payload);
}

/**
 * Inserta un lote de leads (import CSV). No devuelve las filas creadas.
 * Cubre: app/routes/contactos.tsx:353 (import CSV por lotes).
 */
export async function insertLeadsBatch(payload: TablesInsert<"leads">[]) {
  return await supabase.from("leads").insert(payload);
}

// ────────────────────────────────────────────────────────────────────────────
// leads — updates / deletes
// ────────────────────────────────────────────────────────────────────────────

/**
 * Actualiza un lead con un payload parcial arbitrario por id.
 * Cubre: app/routes/crm.tsx:189 (intent "move"),
 *        app/routes/crm.tsx:240 (intent "move_cierre", metadata),
 *        app/routes/crm.tsx:280 (intent "update"),
 *        app/routes/crm.tsx:374 (merge_leads, aplicar merge),
 *        app/routes/contacto-detalle.tsx:334 (intent "update").
 */
export async function updateLead(id: string, updates: TablesUpdate<"leads">) {
  return await supabase.from("leads").update(updates).eq("id", id);
}

/**
 * Actualiza únicamente el status de un lead por id.
 * Cubre: app/routes/crm.tsx:227-228 (intent "move_cierre", cambio de etapa).
 */
export async function updateLeadStatus(id: string, status: TablesUpdate<"leads">["status"]) {
  return await supabase.from("leads").update({ status }).eq("id", id);
}

/**
 * Actualiza únicamente el checklist de documentos de un lead por id.
 * Cubre: app/routes/contacto-detalle.tsx:404 (intent "toggle_doc").
 */
export async function updateLeadDocChecklist(
  id: string,
  docChecklist: TablesUpdate<"leads">["doc_checklist"],
) {
  return await supabase.from("leads").update({ doc_checklist: docChecklist }).eq("id", id);
}

/**
 * Elimina un lead por id.
 * Cubre: app/routes/crm.tsx:287 (intent "delete"),
 *        app/routes/crm.tsx:382 (merge_leads, eliminar lead absorbido).
 */
export async function deleteLead(id: string) {
  return await supabase.from("leads").delete().eq("id", id);
}

// ────────────────────────────────────────────────────────────────────────────
// lead_activities — lecturas
// ────────────────────────────────────────────────────────────────────────────

/**
 * Lista las actividades del usuario con el nombre del lead, ordenadas por
 * fecha programada (asc).
 * Cubre: app/routes/crm.tsx:95-99 (clientLoader, activitiesRes).
 */
export async function listActivitiesWithLeadByUser(userId: string) {
  return await supabase
    .from("lead_activities")
    .select("*, leads(name)")
    .eq("user_id", userId)
    .order("scheduled_at", { ascending: true });
}

/**
 * Cuenta las citas pendientes del usuario en un rango de fechas (head + count exact).
 * Cubre: app/routes/crm.tsx:101-108 (clientLoader, citasRes).
 */
export async function countUpcomingCitasByUser(userId: string, fromIso: string, toIso: string) {
  return await supabase
    .from("lead_activities")
    .select("id", { count: "exact", head: true })
    .eq("user_id", userId)
    .eq("kind", "cita")
    .eq("completed", false)
    .gte("scheduled_at", fromIso)
    .lte("scheduled_at", toIso);
}

/**
 * Lista los lead_id de las actividades del usuario creadas desde una fecha dada.
 * Usado para calcular propiedades calientes.
 * Cubre: app/routes/crm.tsx:109-113 (clientLoader, propVisitasRes).
 */
export async function listActivityLeadIdsSince(userId: string, sinceIso: string) {
  return await supabase
    .from("lead_activities")
    .select("lead_id")
    .eq("user_id", userId)
    .gte("created_at", sinceIso);
}

/**
 * Lista las actividades de un lead, ordenadas por creación (desc), limitado a 50.
 * Cubre: app/routes/contacto-detalle.tsx:231-236 (clientLoader, activitiesRes).
 */
export async function listActivitiesByLead(leadId: string) {
  return await supabase
    .from("lead_activities")
    .select("*")
    .eq("lead_id", leadId)
    .order("created_at", { ascending: false })
    .limit(50);
}

// ────────────────────────────────────────────────────────────────────────────
// lead_activities — inserts
// ────────────────────────────────────────────────────────────────────────────

/**
 * Inserta una actividad (payload array). No devuelve la fila creada.
 * Cubre: app/routes/crm.tsx:193 (stage_change en "move"),
 *        app/routes/crm.tsx:204 (cita en "move"),
 *        app/routes/crm.tsx:248 (stage_change en "move_cierre"),
 *        app/routes/crm.tsx:297 (intent "create_activity"),
 *        app/routes/contacto-detalle.tsx:338 (stage_change en "update").
 */
export async function insertActivity(payload: TablesInsert<"lead_activities">) {
  return await supabase.from("lead_activities").insert([payload]);
}

/**
 * Inserta una actividad y devuelve su id (select("id").single()).
 * Cubre: app/routes/contacto-detalle.tsx:356-363 (intent "add_activity").
 */
export async function insertActivityReturningId(payload: TablesInsert<"lead_activities">) {
  return await supabase.from("lead_activities").insert([payload]).select("id").single();
}

// ────────────────────────────────────────────────────────────────────────────
// lead_activities — updates / deletes
// ────────────────────────────────────────────────────────────────────────────

/**
 * Actualiza el flag `completed` de una actividad por id.
 * Cubre: app/routes/crm.tsx:312-315 (intent "toggle_activity"),
 *        app/routes/contacto-detalle.tsx:383-386 (intent "toggle_activity").
 */
export async function updateActivityCompleted(id: string, completed: boolean) {
  return await supabase.from("lead_activities").update({ completed }).eq("id", id);
}

/**
 * Actualiza una actividad con un payload parcial arbitrario por id.
 * Cubre: app/routes/contacto-detalle.tsx:375 (intent "update_activity").
 */
export async function updateActivity(id: string, updates: TablesUpdate<"lead_activities">) {
  return await supabase.from("lead_activities").update(updates).eq("id", id);
}

/**
 * Reasigna todas las actividades de un lead origen a un lead destino (por lead_id).
 * Cubre: app/routes/crm.tsx:379 (merge_leads, reasignar actividades).
 */
export async function reassignActivitiesLead(fromLeadId: string, toLeadId: string) {
  return await supabase
    .from("lead_activities")
    .update({ lead_id: toLeadId })
    .eq("lead_id", fromLeadId);
}

/**
 * Elimina una actividad por id.
 * Cubre: app/routes/crm.tsx:322 (intent "delete_activity"),
 *        app/routes/contacto-detalle.tsx:393 (intent "delete_activity").
 */
export async function deleteActivity(id: string) {
  return await supabase.from("lead_activities").delete().eq("id", id);
}

// ────────────────────────────────────────────────────────────────────────────
// lead_source_options
// ────────────────────────────────────────────────────────────────────────────

/**
 * Lista las opciones de origen personalizadas (no default) del usuario, ordenadas
 * por creación (asc).
 * Cubre: app/components/SourceCombobox.tsx:33-38 (load).
 */
export async function listCustomSourceOptions(userId: string) {
  return await supabase
    .from("lead_source_options")
    .select("label")
    .eq("user_id", userId)
    .eq("is_default", false)
    .order("created_at", { ascending: true });
}

/**
 * Inserta (upsert) una opción de origen personalizada para el usuario.
 * Cubre: app/components/SourceCombobox.tsx:75-77 (handleAdd).
 */
export async function upsertSourceOption(userId: string, label: string) {
  return await supabase
    .from("lead_source_options")
    .upsert({ user_id: userId, label, is_default: false }, { onConflict: "user_id,label" });
}

/**
 * Elimina una opción de origen personalizada (no default) del usuario por label.
 * Cubre: app/components/SourceCombobox.tsx:89-94 (handleDelete).
 */
export async function deleteSourceOption(userId: string, label: string) {
  return await supabase
    .from("lead_source_options")
    .delete()
    .eq("user_id", userId)
    .eq("label", label)
    .eq("is_default", false);
}

// ────────────────────────────────────────────────────────────────────────────
// lead_assignment_config
// ────────────────────────────────────────────────────────────────────────────

/**
 * Obtiene la config de asignación de un broker (mode, enabled, rr_index), maybeSingle.
 * Cubre: app/routes/equipo.tsx:152 (loadAsignacion, configRes).
 */
export async function getAssignmentConfig(brokerUserId: string) {
  return await supabase
    .from("lead_assignment_config")
    .select("mode,enabled,rr_index")
    .eq("broker_user_id", brokerUserId)
    .maybeSingle();
}

/**
 * Actualiza mode/enabled de la config de asignación de un broker y devuelve las filas
 * afectadas (.select()).
 * Cubre: app/routes/equipo.tsx:278-282 (handleSaveConfig, update).
 */
export async function updateAssignmentConfig(
  brokerUserId: string,
  mode: TablesUpdate<"lead_assignment_config">["mode"],
  enabled: TablesUpdate<"lead_assignment_config">["enabled"],
) {
  return await supabase
    .from("lead_assignment_config")
    .update({ mode, enabled })
    .eq("broker_user_id", brokerUserId)
    .select();
}

/**
 * Inserta la config de asignación de un broker (fallback cuando el update no afectó filas).
 * Cubre: app/routes/equipo.tsx:284-286 (handleSaveConfig, insert).
 */
export async function insertAssignmentConfig(
  brokerUserId: string,
  mode: TablesInsert<"lead_assignment_config">["mode"],
  enabled: TablesInsert<"lead_assignment_config">["enabled"],
) {
  return await supabase
    .from("lead_assignment_config")
    .insert({ broker_user_id: brokerUserId, mode, enabled });
}
